import { useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SkillEditor } from "@/components/features/skills/SkillEditor";
import { automationError } from "@/components/features/automations/automationError";
import { libraryQueryKey } from "@/lib/data/react-query/file-library";
import type { LibraryItem } from "@/lib/data/requests/file-library";
import { LibraryContentEditor } from "./LibraryContentEditor";
import { LibraryMoveDialog } from "./LibraryMoveDialog";

export type PersonalFilePanel = {
  kind: "rename" | "delete" | "folder" | "content" | "move" | "createSkill";
  item?: LibraryItem;
};

export function PersonalFileDialog({
  panel,
  name,
  setName,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  panel?: PersonalFilePanel;
  name: string;
  setName: (name: string) => void;
  pending: boolean;
  error: unknown;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const client = useQueryClient();
  return (
    <>
      <Dialog
        open={!!panel && panel.kind !== "move"}
        onOpenChange={(isOpen) => {
          if (!isOpen && !pending) onClose();
        }}
      >
        {panel && panel.kind !== "move" && (
          <DialogContent
            aria-describedby={undefined}
            className={
              panel.kind === "content" || panel.kind === "createSkill"
                ? "max-h-[90vh] overflow-auto sm:max-w-3xl"
                : ""
            }
          >
            <DialogHeader>
              <DialogTitle>
                {panel.kind === "createSkill"
                  ? "Create skill"
                  : panel.kind === "content"
                    ? "Edit SKILL.md"
                    : panel.kind === "folder"
                      ? "New folder"
                      : panel.kind === "rename"
                        ? "Rename"
                        : "Delete file"}
              </DialogTitle>
            </DialogHeader>
            {panel.kind === "createSkill" ? (
              <SkillEditor
                initialScope="personal"
                scopes={["personal"]}
                onClose={() => {
                  void client.invalidateQueries({
                    queryKey: libraryQueryKey,
                  });
                  onClose();
                }}
              />
            ) : panel.kind === "content" && panel.item ? (
              <LibraryContentEditor
                id={panel.item.id}
                skillId={panel.item.skillId ?? undefined}
                onClose={() => {
                  onClose();
                }}
              />
            ) : (
              <>
                {panel.kind === "delete" ? (
                  <p>
                    {"Delete “" +
                      (panel.item?.name ?? "file") +
                      "”? This cannot be undone."}
                  </p>
                ) : (
                  <Input
                    aria-label="Name"
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value);
                    }}
                  />
                )}
                {error && <p role="alert">{automationError(error)}</p>}
                <Button
                  disabled={
                    pending || (panel.kind !== "delete" && !name.trim())
                  }
                  variant={panel.kind === "delete" ? "destructive" : "default"}
                  onClick={onSubmit}
                >
                  {panel.kind === "delete" ? "Delete" : "Save"}
                </Button>
              </>
            )}
          </DialogContent>
        )}
      </Dialog>
      {panel?.kind === "move" && panel.item && (
        <LibraryMoveDialog
          item={panel.item}
          onClose={() => {
            onClose();
          }}
        />
      )}
    </>
  );
}
